import styled, { useTheme } from 'styled-components';
import Button from '../../../../components/Button';
import ColorizedSpan from '../../../../components/Colorized';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import WidthContainer from '../../../../components/WidthContainer';
import {
  PHONE_MEDIA_QUERY,
  useMediaQuery,
} from '../../../../common/responsive';
import { hapticsImpactLight } from '../../../../common/haptic';
import StoreButtons from '../../../../components/StoreButtons';
import { useNavigate } from 'react-router-dom';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-direction: row;
  width: 100%;
  height: fit-content;
  min-height: 500px;
  justify-content: space-between;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    padding-top: 50px;
    text-align: center;
  }
`;

const Title = styled.h1`
  font-size: 64px;
  font-weight: 500;
  line-height: 77px;
  letter-spacing: -2%;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 2.5rem;
    line-height: 2.8rem;
  }
`;

const SubTitle = styled.h2`
  font-size: 24px;
  font-weight: 400;
  line-height: 29px;
  letter-spacing: -2%;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 1rem;
    line-height: 1.2rem;
  }
`;

const Column = styled.div`
  width: 100%;
  display: flex;
  gap: 30px;
  flex-direction: column;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 15px;
  }
`;

const MobileImage = styled.img`
  width: 436px;
  object-fit: cover;
  user-select: none;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
  cursor: pointer;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
    margin-top: 40px;
    margin-bottom: 40px;
  }
`;

const ContainerBackground = styled.div`
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.background.primary} 0%,
    ${({ theme }) => theme.primary.background} 100%
  );
  position: absolute;
  width: 100%;
  left: 0px;
`;

const IMAGER_MAPPER = {
  default: '/landing-v2/mailer-hero.webp',
  stocks: '/landing-v2/tesla-mailer.webp',
  netflix: '/landing-v2/netflix-mailer.webp',
  gold: '/landing-v2/gold-mailer.webp',
  mcdonalds: '/landing-v2/mcdonalds-mailer.webp',
  microsoft: '/landing-v2/microsoft-mailer.webp',
  cocacola: '/landing-v2/cocacola-mailer.webp',
  apple: '/landing-v2/apple-mailer.webp',
};

const HEADLINE_MAPPER = {
  default: 'Crypto',
  stocks: 'Stocks',
  netflix: 'Stocks',
  gold: 'Stocks',
  mcdonalds: 'Stocks',
  microsoft: 'Stocks',
  cocacola: 'Stocks',
  apple: 'Stocks',
};

const StocksSubline = (theme) => (
  <SubTitle style={{ color: theme.text.regular }}>
    Instant, personalized stock gifts. No brokerage accounts, <br /> powered by
    blockchain technology.
  </SubTitle>
);

const SUBLINE_MAPPER = {
  default: (theme) => (
    <SubTitle style={{ color: theme.text.regular }}>
      Personalized gift cards loaded with crypto, stocks, or other digital assets.
    </SubTitle>
  ),
  stocks: StocksSubline,
  netflix: StocksSubline,
  gold: StocksSubline,
  mcdonalds: StocksSubline,
  microsoft: StocksSubline,
  cocacola: StocksSubline,
  apple: StocksSubline,
};

const Hero = () => {
  const backgroundRef = useRef(null);
  const [siblingHeight, setSiblingHeight] = useState(undefined);
  const [searchParams] = useSearchParams();
  const lpd = searchParams.get('lpd');
  const navigate = useNavigate();

  useEffect(() => {
    const updateHeight = () => {
      if (backgroundRef.current) {
        setSiblingHeight(backgroundRef.current.offsetHeight);
      }
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);

    if (backgroundRef.current) {
      observer.observe(backgroundRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const theme = useTheme();

  return (
    <>
      <ContainerBackground ref={backgroundRef}>
        <WidthContainer>
          <Container>
            <Column>
              <Title>
                {lpd ? (
                  <>
                    The Easiest Way <br />
                    to <ColorizedSpan>Gift</ColorizedSpan>{' '}
                    {HEADLINE_MAPPER[lpd]}
                  </>
                ) : (
                  <>
                    Gift Cards <br />
                    for the <ColorizedSpan>Future</ColorizedSpan>
                  </>
                )}
              </Title>
              {SUBLINE_MAPPER[lpd || 'default'](theme)}
              <Button
                style={{ marginTop: isMobile ? '20px' : '0px' }}
                clickAnimation="bouncy"
                onClick={() => {
                  navigate(
                    lpd ? `/app/gifts/create?lpd=${lpd}` : '/app/gifts/create',
                  );
                }}
                fullWidth={isMobile}
              >
                Create a Gift
              </Button>
              <StoreButtons maxWidth='150px' gap='15px' />
            </Column>
            <Column style={{ width: 'fit-content' }}>
              <MobileImage
                onClick={() => {
                  hapticsImpactLight();
                  navigate(
                    lpd ? `/app/gifts/create?lpd=${lpd}` : '/app/gifts/create',
                  );
                }}
                src={IMAGER_MAPPER[lpd || 'default']}
              />
            </Column>
          </Container>
        </WidthContainer>
      </ContainerBackground>
      <div style={{ height: siblingHeight }} />
    </>
  );
};

export default Hero;
